// src/pages/marketplace/MarketplacePage.jsx
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FaStar,
  FaSlidersH,
  FaMapMarkerAlt,
  FaListUl,
  FaMapMarkedAlt,
  FaSearch,
  FaShieldAlt,
  FaTimes,
  FaArrowRight,
  FaLongArrowAltRight,
  FaRegCircle,
} from 'react-icons/fa';
import { FiCalendar, FiPackage } from 'react-icons/fi';
import './Marketplace.css';
import { listPublishedPublications } from '../../services/publicationsApi';
import { listPublishedRequests } from '../../services/requestsApi';
import usePromotionLookup from '../../hooks/usePromotionLookup';
import { listUnits, UNIT_TYPES } from '../../services/unitsApi';
import { MapContainer, TileLayer, Marker, Popup, useMap, Polyline, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import useActivityMarkers from '../../hooks/useActivityMarkers';
import {
  buildUnitLookups,
  findUnitForPublication,
  getUnitDescriptorParts,
  UNIT_TYPE_LABELS,
} from '../../utils/unitMatching';
import { getPublicationTypeLabel, normalizePublicationType, PUBLICATION_TYPES } from '../../constants/publicationTypes';
import { SERVICE_TYPE_LABELS } from '../../utils/feedItems';
import { resolveLocationLabel } from '../../services/unlocodeService';

const PLACEHOLDER_IMAGE = '/Images/Proyecto.png';
const DEFAULT_FREIGHT_PLACEHOLDER = '/Images/Caja53.png';

const MAP_DEFAULT_CENTER = [23.6345, -102.5528];
const MAP_DEFAULT_ZOOM = 5;
const MAP_MAX_ZOOM = 14;
const MAP_ZOOM_INCREMENT = 2;
const MAP_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const MAP_TILE_ATTRIBUTION = '&copy; OpenStreetMap contributors';
const DATE_KEY_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const FALLBACK_THUMBNAIL =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="120" height="80" viewBox="0 0 120 80" fill="none"><rect width="120" height="80" rx="12" fill="%23e5e7eb"/><path d="M28 52h64l-8-16-14 10-12-18-30 24z" fill="%239ca3af"/><circle cx="42" cy="30" r="8" fill="%23cbd5f5"/></svg>';

const formatMarkerLabel = value => {
  if (!value) return 'Ver';
  const raw = String(value).trim();
  if (!raw) return 'Ver';
  const currencyMatch = raw.match(/([$€£¥]?[\s]?[0-9]+[0-9.,KkMm]*)/);
  if (currencyMatch && currencyMatch[0].length <= 8) {
    return currencyMatch[0].replace(/\s+/g, '');
  }
  const firstToken = raw.split(/\s+/)[0];
  return firstToken.length > 10 ? `${firstToken.slice(0, 9)}…` : firstToken;
};

const clusterItemsShareCoords = marker => {
  if (!marker || !Array.isArray(marker.items) || !marker.items.length) return false;
  return marker.items.every(entry => {
    const lat = Number(entry.latitude ?? entry.lat ?? marker.latitude);
    const lng = Number(entry.longitude ?? entry.lng ?? marker.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
    return Math.abs(lat - marker.latitude) < 0.0001 && Math.abs(lng - marker.longitude) < 0.0001;
  });
};

const useImageFallback = () => {
  return useCallback(event => {
    if (!event?.currentTarget) return;
    event.currentTarget.onerror = null;
    event.currentTarget.src = FALLBACK_THUMBNAIL;
    event.currentTarget.classList.add('marketplace-map-popup__img--fallback');
  }, []);
};

const parsePriceToNumber = value => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value !== 'string') return 0;
  const onlyDigits = value.replace(/[^\d.,]/g, '').replace(/,/g, '');
  const parsed = parseFloat(onlyDigits);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatDate = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const formatRequestBudget = value => {
  const fallbackLabel = 'A Cotizar';
  const formatAmount = (amount, currencyCode = 'MXN') => {
    if (!Number.isFinite(amount) || amount <= 0) return fallbackLabel;
    const formatter = new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: currencyCode.toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return `${formatter.format(amount)} ${currencyCode.toUpperCase()}`;
  };

  if (value === null || value === undefined || value === '') {
    return fallbackLabel;
  }

  if (typeof value === 'object') {
    const amount = Number(value.amount ?? value.value ?? value.total);
    const currency = value.currency || value.code || value.iso || 'MXN';
    if (Number.isFinite(amount)) {
      return formatAmount(amount, currency);
    }
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return formatAmount(value, 'MXN');
  }

  const text = String(value).trim();
  if (!text) return fallbackLabel;

  const normalizedText = text.toLowerCase();
  let detectedCurrency = 'MXN';
  if (normalizedText.includes('usd')) detectedCurrency = 'USD';
  else if (normalizedText.includes('mxn')) detectedCurrency = 'MXN';

  const numericPortion = text.replace(/[^\\d.,-]/g, '');
  if (numericPortion) {
    let normalized = numericPortion;
    if (normalized.includes(',') && normalized.includes('.')) {
      const lastComma = normalized.lastIndexOf(',');
      const lastDot = normalized.lastIndexOf('.');
      normalized = lastDot > lastComma
        ? normalized.replace(/,/g, '')
        : normalized.replace(/\./g, '').replace(',', '.');
    } else if (normalized.includes(',')) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = normalized.replace(/,/g, '');
    }

    const parsed = parseFloat(normalized);
    if (Number.isFinite(parsed)) {
      return formatAmount(parsed, detectedCurrency);
    }
  }

  return text;
};

const parseRequestBudgetNumber = value => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'object') {
    const amount = Number(value.amount ?? value.value ?? value.total);
    return Number.isFinite(amount) ? amount : 0;
  }
  if (typeof value !== 'string') {
    return 0;
  }

  const text = value.trim();
  if (!text) return 0;
  const numericPortion = text.replace(/[^\d.,-]/g, '');
  if (!numericPortion) return 0;

  let normalized = numericPortion;
  if (normalized.includes(',') && normalized.includes('.')) {
    const lastComma = normalized.lastIndexOf(',');
    const lastDot = normalized.lastIndexOf('.');
    normalized =
      lastDot > lastComma
        ? normalized.replace(/,/g, '')
        : normalized.replace(/\./g, '').replace(',', '.');
  } else if (normalized.includes(',')) {
    normalized = normalized.replace(/\./g, '').replace(',', '.');
  } else {
    normalized = normalized.replace(/,/g, '');
  }

  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatBudgetAmountOnly = (value, currency = 'MXN') => {
  const amount = parseRequestBudgetNumber(value);
  if (!amount) {
    return '';
  }
  const normalizedCurrency =
    typeof currency === 'string' && currency.trim().length === 3
      ? currency.trim().toUpperCase()
      : 'MXN';
  const locale = normalizedCurrency === 'USD' ? 'en-US' : 'es-MX';
  return amount.toLocaleString(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const formatRequestDistanceHint = request => {
  const normalize = candidate => {
    if (candidate === null || candidate === undefined) return '';
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return `${candidate.toLocaleString('es-MX')} km`;
    }
    const text = String(candidate).trim();
    return text;
  };

  const candidates = [
    normalize(request?.distanceLabel),
    normalize(request?.distance),
    normalize(request?.distanceHint),
    normalize(request?.estimatedDistance),
    normalize(request?.metadata?.distance),
  ].filter(Boolean);

  return candidates[0] || '450 km';
};

const resolveRequestReferenceDate = request =>
  request?.neededBy || request?.deadline || request?.deliveryDate || request?.created || '';

const isRequestExpired = request => {
  const reference = resolveRequestReferenceDate(request);
  if (!reference) return false;
  const parsed = new Date(reference);
  if (Number.isNaN(parsed.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parsed.getTime() < today.getTime();
};

const simplifyLocationLabel = label => {
  if (!label) return '';
  const raw = String(label);
  if (!raw.trim()) return '';
  const [firstChunk] = raw.split(',');
  const [hyphenChunk] = firstChunk.split(' - ');
  const [pipeChunk] = hyphenChunk.split('|');
  return pipeChunk.replace(/\s+/g, ' ').trim();
};

const formatCityLabel = label => {
  if (!label) {
    return '';
  }
  return label
    .split(' ')
    .map(part => {
      const trimmed = part.trim();
      if (!trimmed) return trimmed;
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
    })
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const normalizeFilterValue = value =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/\s+/g, ' ')
    .trim();

const CATEGORY_LABEL_BY_SLUG = PUBLICATION_TYPES.reduce((accumulator, type) => {
  accumulator[type.value] = type.label;
  return accumulator;
}, {});

const CATEGORY_SLUG_BY_LABEL = PUBLICATION_TYPES.reduce((accumulator, type) => {
  accumulator[normalizeFilterValue(type.label)] = type.value;
  return accumulator;
}, {});

const resolveCategoryQueryValue = (labelRaw, slugRaw) => {
  const slugCandidate = String(slugRaw || '').trim();
  if (slugCandidate) {
    const normalizedSlug = normalizePublicationType(slugCandidate);
    const resolvedLabel =
      CATEGORY_LABEL_BY_SLUG[normalizedSlug] || getPublicationTypeLabel(normalizedSlug);
    if (resolvedLabel) {
      return { label: resolvedLabel, slug: normalizedSlug };
    }
  }

  const labelCandidate = String(labelRaw || '').trim();
  if (!labelCandidate) {
    return { label: '', slug: '' };
  }
  const normalizedLabel = normalizeFilterValue(labelCandidate);
  const resolvedSlug = CATEGORY_SLUG_BY_LABEL[normalizedLabel] || '';
  return { label: labelCandidate, slug: resolvedSlug };
};

const getCategorySlugFromLabel = label => {
  if (!label) return '';
  const normalized = normalizeFilterValue(label);
  return CATEGORY_SLUG_BY_LABEL[normalized] || '';
};

const normalizeUnitTypeValue = value =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const expandUnitTypeSelection = value => {
  const normalizedValue = normalizeUnitTypeValue(value);
  const match = UNIT_TYPES.find(option => {
    if (!option) return false;
    if (option.value === value) return true;
    const normalizedLabel = normalizeUnitTypeValue(option.label);
    return normalizedLabel === normalizedValue;
  });

  if (!match) {
    return [value];
  }

  const variants = new Set([match.value, match.label]);
  return Array.from(variants);
};

const normalizeDateKey = value => {
  if (!value) return '';
  const str = String(value).trim();
  if (!str) return '';
  const candidate = str.slice(0, 10);
  if (DATE_KEY_REGEX.test(candidate)) {
    return candidate;
  }
  const parsed = new Date(str);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isDateWithinRange = (dateKey, startValue, endValue) => {
  if (!dateKey) return false;
  const startKey = normalizeDateKey(startValue);
  if (!startKey) return false;
  const tentativeEnd = normalizeDateKey(endValue);
  const endKey = tentativeEnd && tentativeEnd >= startKey ? tentativeEnd : startKey;
  return dateKey >= startKey && dateKey <= endKey;
};

const matchesAvailabilityOnDate = (item, dateKey) => {
  if (!dateKey) return true;
  const ranges = Array.isArray(item?.unitAvailabilityRanges) ? item.unitAvailabilityRanges : [];
  const hasRangeMatch = ranges.some(range => {
    if (!range) return false;
    return isDateWithinRange(dateKey, range.start, range.end);
  });
  if (hasRangeMatch) {
    return true;
  }
  const availableFromKey = normalizeDateKey(item?.availableFrom);
  const availableToKey = normalizeDateKey(item?.availableTo);
  if (availableFromKey && availableToKey) {
    return isDateWithinRange(dateKey, availableFromKey, availableToKey);
  }
  if (availableFromKey && !availableToKey) {
    return dateKey >= availableFromKey;
  }
  if (!availableFromKey && availableToKey) {
    return dateKey <= availableToKey;
  }
  return false;
};

const resolveMarketplaceQueryParams = search => {
  const params = new URLSearchParams(search || '');
  const quickSearch = params.get('quickSearch') || '';
  const origin = params.get('origin') || '';
  const segmentParam = params.get('segment') === 'requests' ? 'requests' : 'publications';
  const categoryParam = params.get('category') || '';
  const categorySlugParam = params.get('categorySlug') || '';
  const resolvedCategory = resolveCategoryQueryValue(categoryParam, categorySlugParam);
  const locationFilter = params.get('location') || '';
  const unitTypes = Array.from(
    new Set(
      params
        .getAll('unitType')
        .filter(Boolean)
        .flatMap(expandUnitTypeSelection)
    )
  );

  return {
    quickSearch,
    hasQuickSearch: params.has('quickSearch'),
    origin,
    hasOrigin: params.has('origin'),
    category: resolvedCategory.label,
    categorySlug: resolvedCategory.slug,
    hasCategory: Boolean(resolvedCategory.label),
    location: locationFilter,
    hasLocation: params.has('location') && Boolean(locationFilter.trim()),
    unitTypes,
    hasUnitTypes: unitTypes.length > 0,
    segment: segmentParam,
  };
};

export function MarketplaceContent({ withinDashboard = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = useMemo(
    () => resolveMarketplaceQueryParams(location.search),
    [location.search]
  );
  const queryStateRef = useRef(queryParams);
  const paginationScrollGuardRef = useRef(false);
  const filtersScrollGuardRef = useRef(false);
  const scrollToMarketplaceTop = useCallback(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const shell = document.getElementById('marketplace-shell');
    const topOffset = shell
      ? Math.max(shell.getBoundingClientRect().top + window.scrollY - 20, 0)
      : 0;
    window.scrollTo({
      top: topOffset,
      behavior: 'smooth',
    });
  }, []);
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState('');
  const [units, setUnits] = useState([]);
  const { isPromoted } = usePromotionLookup();

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await listPublishedPublications({ limit: 96 });
        if (!ignore) {
          setPublications(response);
        }
      } catch (err) {
        if (!ignore) {
          setError(err?.message || 'No pudimos cargar el marketplace en este momento.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    let ignore = false;
    const fetchRequests = async () => {
      setRequestsLoading(true);
      setRequestsError('');
      try {
        const response = await listPublishedRequests({ limit: 96 });
        if (!ignore) {
          setRequests(response);
        }
      } catch (err) {
        if (!ignore) {
          setRequestsError(err?.message || 'No pudimos cargar las solicitudes publicadas.');
        }
      } finally {
        if (!ignore) {
          setRequestsLoading(false);
        }
      }
    };

    fetchRequests();
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    const fetchUnits = async () => {
      try {
        const response = await listUnits();
        if (!ignore) {
          setUnits(response);
        }
      } catch (unitError) {
        if (!ignore) {
          console.error('No se pudieron cargar las unidades publicadas.', unitError);
          setUnits([]);
        }
      }
    };

    fetchUnits();

    return () => {
      ignore = true;
    };
  }, []);

  const unitLookups = useMemo(() => buildUnitLookups(units), [units]);

  const cards = useMemo(() => {
    return publications.map(item => {
      const linkedUnit = findUnitForPublication(item, unitLookups);
      const hasContent = value =>
        typeof value === 'string' ? value.trim().length > 0 : Boolean(value);

      const pickImage = (...candidates) => {
        const found = candidates.find(src => hasContent(src));
        return found && typeof found === 'string' ? found.trim() : found || '';
      };

      const unitPhotoUrl = pickImage(
        linkedUnit?.photoUrl,
        linkedUnit?.photo,
        item.unitPhotoUrl
      );
      const descriptor = getUnitDescriptorParts(linkedUnit).join(' - ');
      const resolvedUnitTypeValue =
        linkedUnit?.tractorType ||
        (item.unitTractorType && UNIT_TYPE_LABELS[item.unitTractorType]
          ? item.unitTractorType
          : item.unitTractorType || '');
      const unitTypeValue = resolvedUnitTypeValue || '';
      const unitTypeLabel = unitTypeValue
        ? UNIT_TYPE_LABELS[unitTypeValue] ||
          linkedUnit?.tractorType ||
          item.unitTractorTypeLabel ||
          item.unitTractorType ||
          ''
        : item.unitTractorTypeLabel || '';
      const displayName =
        linkedUnit?.model ||
        linkedUnit?.tractorType ||
        item.unitName ||
        item.title ||
        'Publicacion';
      const companyImage = pickImage(
        item.companyHeroUrl,
        item.companyLogoUrl,
        item.companyBannerUrl
      );

      const isContainerType = value =>
        typeof value === 'string' && value.toLowerCase().includes('container');

      const imageUrl =
        pickImage(
          item.primaryImageUrl,
          item.productImageUrl,
          item.bannerUrl,
          unitPhotoUrl
        ) ||
        companyImage ||
        (isContainerType(unitTypeValue) ? DEFAULT_FREIGHT_PLACEHOLDER : PLACEHOLDER_IMAGE);
      const priceNumber = parsePriceToNumber(item.price);
      const category = getPublicationTypeLabel(item.type);
      const promoted = isPromoted('publication', item.id);
      const verified = Boolean(item?.company?.verified || promoted || item?.rating >= 4.5);
      const rawLocationLabel = item.location || item.unitName || '';
      const resolvedLocationLabel = rawLocationLabel || 'Ubicacion por confirmar';
      const locationSimple = simplifyLocationLabel(rawLocationLabel);
      return {
        id: item.id,
        slug: item.numericId || item.id,
        publicId: item.numericId || item.id,
        proveedor: category,
        nombre: item.title || 'Publicacion sin titulo',
        img: imageUrl,
        imgAlt: `Foto de ${displayName}`,
        companyName: item.companyName || '',
        rating: 4.8,
        fecha: item.updated || item.created,
        precio: priceNumber,
        ubic: resolvedLocationLabel,
        locationSimple,
        categoria: category,
        priceLabel: item.price && String(item.price).trim() ? item.price : 'Consulta precio',
        descriptor,
        promoted,
        verified,
        unitTypeValue,
        unitTypeLabel,
        unitAvailabilityRanges: Array.isArray(item.unitAvailabilityRanges)
          ? item.unitAvailabilityRanges
          : [],
        availableFrom: item.availableFrom || '',
        availableTo: item.availableTo || '',
      };
    });
  }, [publications, unitLookups, isPromoted]);

  const requestCards = useMemo(() => {
    return requests.map(item => {
      const pickupLabel = item.pickupLocation || 'Origen por definir';
      const dropoffLabel = item.dropoffLocation || '';
      const pickupSimple = simplifyLocationLabel(pickupLabel);
      const dropoffSimple = simplifyLocationLabel(dropoffLabel);
      const pickupDisplay = formatCityLabel(pickupSimple) || pickupSimple || 'Origen por definir';
      const dropoffDisplay = formatCityLabel(dropoffSimple) || dropoffSimple || 'Destino por definir';
      const neededByLabel = formatDate(item.neededBy);
      const serviceTypeLabel = SERVICE_TYPE_LABELS[item.serviceType] || 'Servicio';
      const budgetLabel = formatRequestBudget(item.budget);
      const currencyCode =
        typeof item.currency === 'string' && item.currency.trim().length === 3
          ? item.currency.trim().toUpperCase()
          : 'MXN';
      const budgetAmount = formatBudgetAmountOnly(item.budget, currencyCode);
      const expired = isRequestExpired(item);
      const loadTypeLabel = item.loadTypeLabel || item.cargoType || serviceTypeLabel;
      const originCoordinates = item.pickupCoordinates || item.originCoordinates || null;
      const destinationCoordinates = item.dropoffCoordinates || item.destinationCoordinates || null;
      return {
        id: item.id,
        title: item.title || 'Solicitud sin titulo',
        companyName: item.company?.name || '',
        pickupLocation: pickupLabel,
        pickupLocationSimple: pickupSimple,
        pickupLocationDisplay: pickupDisplay,
        dropoffLocation: dropoffLabel,
        dropoffLocationSimple: dropoffSimple,
        dropoffLocationDisplay: dropoffDisplay,
        neededBy: item.neededBy || '',
        neededByLabel,
        updated: item.updated || item.created,
        budgetLabel,
        budgetAmount,
        currencyCode,
        serviceTypeLabel,
        loadTypeLabel,
        distanceLabel: formatRequestDistanceHint(item),
        originCoordinates,
        destinationCoordinates,
        expired,
        href: `/solicitudes/${item.id}`,
      };
    });
  }, [requests]);

  // Estado filtros y paginacion (no usamos URL)
  const [term, setTerm] = useState(() => queryParams.quickSearch);
  const [origin, setOrigin] = useState(() => queryParams.origin);
  const [selectedCategories, setCategories] = useState(() =>
    queryParams.hasCategory ? [queryParams.category] : []
  );
  const [selectedLocations, setSelectedLocations] = useState(() =>
    queryParams.hasLocation ? [queryParams.location] : []
  );
  const [selectedUnitTypes, setSelectedUnitTypes] = useState(() => [
    ...queryParams.unitTypes,
  ]);
  const [selectedRatings, setRatings] = useState([]);
  const [onlyVerified, setOnlyVerified] = useState(false);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [sortOption, setSortOption] = useState('recent');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false); // movil
  const [viewMode, setViewMode] = useState('classic');
  const [marketSegment, setMarketSegment] = useState(() => queryParams.segment || 'publications');
  const [collapsedSections, setCollapsedSections] = useState({
    categories: false,
    locations: false,
    availability: false,
    ratings: false,
    verified: false,
  });
  const itemsPerPage = 12;

  useEffect(() => {
    if (withinDashboard) return;
    const nextSegment = queryParams.segment || 'publications';
    if (nextSegment !== marketSegment) {
      setMarketSegment(nextSegment);
    }
  }, [queryParams.segment, marketSegment, withinDashboard]);

  const handleSegmentChange = useCallback(
    nextSegment => {
      setMarketSegment(nextSegment);
      if (withinDashboard) return;
      const params = new URLSearchParams(location.search);
      if (nextSegment === 'publications') {
        params.delete('segment');
      } else {
        params.set('segment', nextSegment);
      }
      const nextSearch = params.toString();
      navigate(
        `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`,
        { replace: true }
      );
    },
    [location.pathname, location.search, navigate, withinDashboard]
  );

  useEffect(() => {
    setCurrentPage(1);
    if (marketSegment === 'requests') {
      setViewMode('classic');
    }
  }, [marketSegment]);

  const categoriesList = useMemo(() => {
    return [...new Set(cards.map((u) => (u.categoria || '').trim()).filter(Boolean))];
  }, [cards]);
  const categoriesListWithAll = useMemo(() => ['Todos', ...categoriesList], [categoriesList]);
  const { locationEntries, totalLocationCount } = useMemo(() => {
    const stats = new Map();
    cards.forEach(card => {
      const simplified = card?.locationSimple || simplifyLocationLabel(card?.ubic || '');
      if (!simplified) return;
      stats.set(simplified, (stats.get(simplified) || 0) + 1);
    });
    const entries = Array.from(stats.entries())
      .map(([value, count]) => ({
        value,
        count,
        displayLabel: `${value} (${count})`,
      }))
      .sort((a, b) => a.value.localeCompare(b.value, 'es', { sensitivity: 'base' }));
    const total = entries.reduce((sum, entry) => sum + entry.count, 0);
    return { locationEntries: entries, totalLocationCount: total };
  }, [cards]);
  const locationOptions = useMemo(() => {
    return [
      {
        value: 'Todas',
        count: totalLocationCount,
        displayLabel: `Todas (${totalLocationCount})`,
      },
      ...locationEntries,
    ];
  }, [locationEntries, totalLocationCount]);

  useEffect(() => {
    if (!queryParams.hasCategory || !categoriesList.length) {
      return;
    }
    const normalizedTarget = normalizeFilterValue(queryParams.category);
    const match = categoriesList.find(
      category => normalizeFilterValue(category) === normalizedTarget
    );
    if (match) {
      setCategories(prev =>
        prev.length === 1 && prev[0] === match ? prev : [match]
      );
    }
  }, [categoriesList, queryParams.category, queryParams.hasCategory]);

  useEffect(() => {
    if (!queryParams.hasLocation || !locationEntries.length) {
      return;
    }
    const normalizedTarget = normalizeFilterValue(queryParams.location);
    const match = locationEntries.find(
      entry => normalizeFilterValue(entry.value) === normalizedTarget
    );
    if (match) {
      setSelectedLocations(prev =>
        prev.length === 1 && prev[0] === match.value ? prev : [match.value]
      );
    }
  }, [locationEntries, queryParams.location, queryParams.hasLocation]);

  const ratingPresets = [4, 3, 2];

  const selectedDateLabel = useMemo(() => {
    if (!selectedDate) return '';
    const normalized = normalizeDateKey(selectedDate);
    if (!normalized) return '';
    const [year, month, day] = normalized.split('-').map(part => Number(part));
    if (!year || !month || !day) return '';
    const date = new Date(year, month - 1, day);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }, [selectedDate]);

  const minAvailabilityDate = useMemo(() => {
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return normalizeDateKey(tomorrow);
  }, []);

  const handleAvailabilityDateChange = useCallback(
    value => {
      if (!value) {
        setSelectedDate('');
        return;
      }
      const nextValue = Array.isArray(value) ? value[0] : value;
      const normalized = normalizeDateKey(nextValue);
      if (!normalized) return;
      if (minAvailabilityDate && normalized < minAvailabilityDate) {
        return;
      }
      setSelectedDate(normalized);
    },
    [minAvailabilityDate]
  );

  const handleClearSelectedDate = useCallback(() => {
    setSelectedDate('');
  }, []);

  useEffect(() => {
    if (selectedDate && minAvailabilityDate && selectedDate < minAvailabilityDate) {
      setSelectedDate('');
    }
  }, [selectedDate, minAvailabilityDate]);

  const handleCategorySelection = useCallback(
    value => {
      if (value === 'Todos') {
        setCategories([]);
        if (!withinDashboard) {
          const params = new URLSearchParams(location.search);
          params.delete('category');
          params.delete('categorySlug');
          const nextSearch = params.toString();
          navigate(
            `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`,
            { replace: true }
          );
        }
        return;
      }
      setCategories([value]);
      if (!withinDashboard) {
        const params = new URLSearchParams(location.search);
        params.set('category', value);
        const slug = getCategorySlugFromLabel(value);
        if (slug) {
          params.set('categorySlug', slug);
        } else {
          params.delete('categorySlug');
        }
        const nextSearch = params.toString();
        navigate(
          `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`,
          { replace: true }
        );
      }
    },
    [location.pathname, location.search, navigate, withinDashboard]
  );

  const handleLocationSelection = useCallback(
    value => {
      if (value === 'Todas') {
        setSelectedLocations([]);
        if (!withinDashboard) {
          const params = new URLSearchParams(location.search);
          params.delete('location');
          const nextSearch = params.toString();
          navigate(
            `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`,
            { replace: true }
          );
        }
        return;
      }
      setSelectedLocations([value]);
      if (!withinDashboard) {
        const params = new URLSearchParams(location.search);
        params.set('location', value);
        const nextSearch = params.toString();
        navigate(
          `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`,
          { replace: true }
        );
      }
    },
    [location.pathname, location.search, navigate, withinDashboard]
  );

  const toggleSectionVisibility = useCallback(sectionKey => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  }, []);

  const handlePresetRating = useCallback(
    rating => {
      setRatings(prev => (prev.includes(rating) ? prev.filter(r => r !== rating) : [rating]));
    },
    []
  );

  const clearFilters = () => {
    setTerm('');
    setOrigin('');
    setCategories([]);
    setSelectedLocations([]);
    setSelectedUnitTypes([]);
    setRatings([]);
    setMinPrice('');
    setMaxPrice('');
    setSelectedDate('');
    setSortOption('recent');
    setCurrentPage(1);
    const params = new URLSearchParams(location.search);
    if (params.has('quickSearch')) {
      params.delete('quickSearch');
    }
    if (params.has('origin')) {
      params.delete('origin');
    }
    if (params.has('category')) {
      params.delete('category');
    }
    if (params.has('categorySlug')) {
      params.delete('categorySlug');
    }
    if (params.has('location')) {
      params.delete('location');
    }
    params.delete('unitType');
    const nextSearch = params.toString();
    navigate(
      `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`,
      { replace: true }
    );
  };

  const filteredPublications = useMemo(() => {
    const nMin = minPrice !== '' ? Number(minPrice) : null;
    const nMax = maxPrice !== '' ? Number(maxPrice) : null;
    const selectedDateKey = normalizeDateKey(selectedDate);

    let res = cards.filter((u) => {
      const name = (u.nombre || '').toLowerCase();
      const ub   = (u.ubic || '').toLowerCase();
      const locationLabel = u.locationSimple || simplifyLocationLabel(u.ubic || '');

      const mTerm = term ? name.includes(term.toLowerCase()) : true;
      const mOrig = origin ? ub.includes(origin.toLowerCase()) : true;
      const mCat = selectedCategories.length ? selectedCategories.includes(u.categoria) : true;
      const mLocation = selectedLocations.length
        ? selectedLocations.includes(locationLabel)
        : true;
      const mUnitType = selectedUnitTypes.length
        ? selectedUnitTypes.includes(u.unitTypeValue)
        : true;
      const mRate = selectedRatings.length
        ? selectedRatings.includes(Math.floor(u.rating || 0))
        : true;
      const mVerified = onlyVerified ? u.verified : true;
      const mAvailability = matchesAvailabilityOnDate(u, selectedDateKey);

      const price = Number(u.precio) || 0;
      const mMin = nMin !== null ? price >= nMin : true;
      const mMax = nMax !== null ? price <= nMax : true;

      return (
        mTerm &&
        mOrig &&
        mCat &&
        mLocation &&
        mUnitType &&
        mRate &&
        mVerified &&
        mAvailability &&
        mMin &&
        mMax
      );
    });

    res.sort((a, b) => {
      switch (sortOption) {
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'priceAsc':
          return (a.precio || 0) - (b.precio || 0);
        case 'priceDesc':
          return (b.precio || 0) - (a.precio || 0);
        case 'recent':
        default: {
          const ta = new Date(a.fecha).getTime() || 0;
          const tb = new Date(b.fecha).getTime() || 0;
          return tb - ta;
        }
      }
    });

    return res;
  }, [
    cards,
    term,
    origin,
    selectedCategories,
    selectedUnitTypes,
    selectedLocations,
    selectedRatings,
    minPrice,
    maxPrice,
    selectedDate,
    sortOption,
    onlyVerified,
  ]);

  const filteredRequests = useMemo(() => {
    const search = term.trim().toLowerCase();
    const searchOrigin = origin.trim().toLowerCase();
    return requestCards
      .filter(item => {
        const textHaystack = `${item.title} ${item.companyName} ${item.serviceTypeLabel}`.toLowerCase();
        const originHaystack = `${item.pickupLocation} ${item.dropoffLocation}`.toLowerCase();
        const matchesTerm = search ? textHaystack.includes(search) : true;
        const matchesOrigin = searchOrigin ? originHaystack.includes(searchOrigin) : true;
        const matchesLocation = selectedLocations.length
          ? selectedLocations.some(
              loc =>
                loc === item.pickupLocationSimple ||
                (item.dropoffLocationSimple && loc === item.dropoffLocationSimple)
            )
          : true;
        return matchesTerm && matchesOrigin && matchesLocation;
      })
      .sort((a, b) => {
        const ta = new Date(a.updated).getTime() || 0;
        const tb = new Date(b.updated).getTime() || 0;
        return tb - ta;
      });
  }, [requestCards, term, origin, selectedLocations]);

  const isRequestsMode = marketSegment === 'requests';
  const activeCollection = isRequestsMode ? filteredRequests : filteredPublications;
  const totalPages = Math.max(1, Math.ceil(activeCollection.length / itemsPerPage));
  const paginated = useMemo(
    () =>
      activeCollection.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      ),
    [activeCollection, currentPage]
  );

  const resultsCount = activeCollection.length;
  const activeLoading = isRequestsMode ? requestsLoading : loading;
  const activeError = isRequestsMode ? requestsError : error;

  // Si cambian los resultados, regresamos a la pagina 1
  useEffect(() => {
    setCurrentPage(1);
  }, [
    term,
    origin,
    selectedCategories,
    selectedLocations,
    selectedUnitTypes,
    selectedRatings,
    minPrice,
    maxPrice,
    selectedDate,
    sortOption,
    onlyVerified,
  ]);

  // UX: al aplicar/quitar filtros, regresar al inicio del listado
  useEffect(() => {
    if (withinDashboard) return;
    if (!filtersScrollGuardRef.current) {
      filtersScrollGuardRef.current = true;
      return;
    }
    scrollToMarketplaceTop();
  }, [
    term,
    origin,
    selectedCategories,
    selectedLocations,
    selectedUnitTypes,
    selectedRatings,
    minPrice,
    maxPrice,
    selectedDate,
    sortOption,
    onlyVerified,
    withinDashboard,
    scrollToMarketplaceTop,
  ]);

  // UX: al cambiar de pagina, sube al inicio del grid
  useEffect(() => {
    if (withinDashboard) return;
    if (!paginationScrollGuardRef.current) {
      paginationScrollGuardRef.current = true;
      return;
    }
    scrollToMarketplaceTop();
  }, [currentPage, withinDashboard, scrollToMarketplaceTop]);

  useEffect(() => {
    const previous = queryStateRef.current;
    queryStateRef.current = queryParams;

    if (queryParams.hasQuickSearch) {
      setTerm(prevTerm =>
        prevTerm === queryParams.quickSearch ? prevTerm : queryParams.quickSearch
      );
    } else if (previous.hasQuickSearch) {
      setTerm(prevTerm => (prevTerm === '' ? prevTerm : ''));
    }

    if (queryParams.hasOrigin) {
      setOrigin(prevOrigin =>
        prevOrigin === queryParams.origin ? prevOrigin : queryParams.origin
      );
    } else if (previous.hasOrigin) {
      setOrigin(prevOrigin => (prevOrigin === '' ? prevOrigin : ''));
    }

    if (queryParams.hasUnitTypes) {
      setSelectedUnitTypes(prev => {
        const nextKey = queryParams.unitTypes.join('|');
        const currentKey = Array.isArray(prev) ? prev.join('|') : '';
        if (currentKey === nextKey) {
          return prev;
        }
        return [...queryParams.unitTypes];
      });
    } else if (previous.hasUnitTypes) {
      setSelectedUnitTypes(prev => (prev.length ? [] : prev));
    }

    if (queryParams.hasCategory) {
      setCategories(prev =>
        prev.length === 1 && prev[0] === queryParams.category ? prev : [queryParams.category]
      );
    } else if (previous.hasCategory) {
      setCategories(prev => (prev.length ? [] : prev));
    }

    if (queryParams.hasLocation) {
      setSelectedLocations(prev =>
        prev.length === 1 && prev[0] === queryParams.location ? prev : [queryParams.location]
      );
    } else if (previous.hasLocation) {
      setSelectedLocations(prev => (prev.length ? [] : prev));
    }
  }, [queryParams]);

  useEffect(() => {
    if (withinDashboard) return;
    if (typeof window === 'undefined') return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [withinDashboard]);

  const rootClassName = withinDashboard
    ? 'marketplace-hub marketplace-hub--dashboard'
    : 'marketplace-hub';

  const modernEmptyState = (
      <div className="marketplace-hub__empty">
        <div className="marketplace-hub__empty-icon">
          <FaSearch aria-hidden="true" />
        </div>
        <h3>No encontramos coincidencias</h3>
        <p>Ajusta tus filtros o intenta con otro término.</p>
        <button type="button" onClick={clearFilters}>
          Limpiar filtros
        </button>
      </div>
    );

  const hasActiveFilters =
      selectedCategories.length > 0 || selectedRatings.length > 0 || onlyVerified;
  const renderRatingStars = rating =>
      Array.from({ length: 5 }, (_, index) => (
        <FaStar
          key={`${rating}-${index}`}
          aria-hidden="true"
          className={index < rating ? 'filled' : ''}
        />
      ));
  const closeFiltersPanel = () => setShowFilters(false);

  return (
    <div className={rootClassName} id="marketplace-shell">
        <div className="marketplace-hub__layout">
          <aside className={`marketplace-hub__filters${showFilters ? ' is-open' : ''}`}>
            <div className="marketplace-hub__filter-card">
              <div className="marketplace-hub__filter-heading">
                <div className="marketplace-hub__filter-icon">
                  <FaSlidersH aria-hidden="true" />
                  </div>
                <div>
                  <strong>Filtros</strong>
                </div>
                  <button
                    type="button"
                    className="marketplace-hub__filters-close"
                    onClick={closeFiltersPanel}
                    aria-label="Cerrar filtros"
                  >
                    <FaTimes aria-hidden="true" />
                  </button>
              </div>

              <div className="marketplace-hub__filter-section marketplace-hub__filter-section--search">
                <form
                  className="marketplace-hub__search"
                  onSubmit={event => event.preventDefault()}
                >
                  <FaSearch aria-hidden="true" />
                  <input
                    type="text"
                    placeholder="Buscar pallets, transporte, aduanas..."
                    value={term}
                    onChange={event => setTerm(event.target.value)}
                  />
                  {term ? (
                    <button type="button" onClick={() => setTerm('')}>
                      Limpiar
                    </button>
                  ) : null}
                </form>
              </div>

              <div className="marketplace-hub__filter-section marketplace-hub__filter-section--segment">
                <div className="marketplace-hub__segments marketplace-hub__segments--stacked">
                  <button
                    type="button"
                    className={`marketplace-hub__segment${marketSegment === 'publications' ? ' is-active' : ''}`}
                    onClick={() => handleSegmentChange('publications')}
                  >
                    Proveedores
                  </button>
                  <button
                    type="button"
                    className={`marketplace-hub__segment${marketSegment === 'requests' ? ' is-active' : ''}`}
                    onClick={() => handleSegmentChange('requests')}
                  >
                    Solicitudes
                  </button>
                </div>
              </div>

              <div className="marketplace-hub__filter-section">
                <button
                  type="button"
                  className="marketplace-hub__section-toggle"
                  onClick={() => toggleSectionVisibility('verified')}
                  aria-expanded={!collapsedSections.verified}
                >
                  <span>Proveedores verificados</span>
                  <span
                    className={`marketplace-hub__section-toggle-icon${
                      collapsedSections.verified ? ' is-collapsed' : ''
                    }`}
                    aria-hidden="true"
                  />
                </button>
                {!collapsedSections.verified ? (
                  <label className="marketplace-hub__switch">
                    <span>Proveedores verificados</span>
                    <div className="marketplace-hub__switch-control">
                      <input
                        type="checkbox"
                        checked={onlyVerified}
                        onChange={() => setOnlyVerified(value => !value)}
                      />
                      <span className="marketplace-hub__switch-slider" />
                    </div>
                  </label>
                ) : null}
              </div>
              <div className="marketplace-hub__filter-section">
                <button
                  type="button"
                  className="marketplace-hub__section-toggle"
                  onClick={() => toggleSectionVisibility('categories')}
                  aria-expanded={!collapsedSections.categories}
                >
                  <span>Categorías</span>
                  <span
                    className={`marketplace-hub__section-toggle-icon${
                      collapsedSections.categories ? ' is-collapsed' : ''
                    }`}
                    aria-hidden="true"
                  />
                </button>
                {!collapsedSections.categories ? (
                  <div className="marketplace-hub__category-list" role="radiogroup">
                    {categoriesListWithAll.map(cat => {
                      const isActive =
                        (cat === 'Todos' && selectedCategories.length === 0) ||
                        selectedCategories.includes(cat);
                      return (
                        <label
                          key={cat}
                          className={`marketplace-hub__category${isActive ? ' is-active' : ''}`}
                        >
                          <input
                            type="radio"
                            name="marketplace-category"
                            value={cat}
                            checked={isActive}
                            onChange={() => handleCategorySelection(cat)}
                          />
                          <span>{cat}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : null}
              </div>
              <div className="marketplace-hub__filter-section">
                <button
                  type="button"
                  className="marketplace-hub__section-toggle"
                  onClick={() => toggleSectionVisibility('locations')}
                  aria-expanded={!collapsedSections.locations}
                >
                  <span>Ubicaciones</span>
                  <span
                    className={`marketplace-hub__section-toggle-icon${
                      collapsedSections.locations ? ' is-collapsed' : ''
                    }`}
                    aria-hidden="true"
                  />
                </button>
                {!collapsedSections.locations ? (
                  <div className="marketplace-hub__category-list" role="radiogroup">
                    {locationOptions.map(option => {
                      const isAll = option.value === 'Todas';
                      const isActive = isAll
                        ? selectedLocations.length === 0
                        : selectedLocations.includes(option.value);
                      return (
                        <label
                          key={option.value}
                          className={`marketplace-hub__category${isActive ? ' is-active' : ''}`}
                        >
                          <input
                            type="radio"
                            name="marketplace-location"
                            value={option.value}
                            checked={isActive}
                            onChange={() => handleLocationSelection(option.value)}
                          />
                          <span>{option.displayLabel}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : null}
              </div>

              <div className="marketplace-hub__filter-section">
                <button
                  type="button"
                  className="marketplace-hub__section-toggle"
                  onClick={() => toggleSectionVisibility('availability')}
                  aria-expanded={!collapsedSections.availability}
                >
                  <span>Disponibilidad</span>
                  <span
                    className={`marketplace-hub__section-toggle-icon${
                      collapsedSections.availability ? ' is-collapsed' : ''
                    }`}
                    aria-hidden="true"
                  />
                </button>
                {!collapsedSections.availability ? (
                  <div className="marketplace-hub__availability">
                    <p className="marketplace-hub__availability-helper">
                      {selectedDateLabel
                        ? `Mostrando resultados disponibles el ${selectedDateLabel}.`
                        : 'Selecciona una fecha para ver resultados disponibles ese día.'}
                    </p>
                    <div className="marketplace-hub__availability-controls">
                      <input
                        type="date"
                        value={selectedDate || ''}
                        min={minAvailabilityDate || undefined}
                        onChange={event => handleAvailabilityDateChange(event.target.value)}
                        aria-label="Seleccionar fecha de disponibilidad"
                      />
                      {selectedDate ? (
                        <button
                          type="button"
                          className="marketplace-hub__availability-clear"
                          onClick={handleClearSelectedDate}
                        >
                          Quitar fecha
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="marketplace-hub__filter-section">
                <button
                  type="button"
                  className="marketplace-hub__section-toggle"
                  onClick={() => toggleSectionVisibility('ratings')}
                  aria-expanded={!collapsedSections.ratings}
                >
                  <span>Calificación</span>
                  <span
                    className={`marketplace-hub__section-toggle-icon${
                      collapsedSections.ratings ? ' is-collapsed' : ''
                    }`}
                    aria-hidden="true"
                  />
                </button>
                {!collapsedSections.ratings ? (
                  <div className="marketplace-hub__rating-chips">
                    {ratingPresets.map(rating => {
                      const isActive = selectedRatings.includes(rating);
                      return (
                        <button
                          type="button"
                          key={rating}
                          className={`marketplace-hub__rating${isActive ? ' is-active' : ''}`}
                          onClick={() => handlePresetRating(rating)}
                        >
                          <span className="marketplace-hub__rating-stars">
                            {renderRatingStars(rating)}
                          </span>
                          <span>{rating}+ & más</span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>

              <button type="button" className="marketplace-hub__clear" onClick={clearFilters}>
                Limpiar filtros
              </button>
            </div>
          </aside>

          <section className="marketplace-hub__content">
            <div className="marketplace-hub__toolbar">
              <div>
                <p className="marketplace-hub__results">
                  {marketSegment === 'requests'
                    ? `${resultsCount} solicitudes`
                    : `${resultsCount} resultados`}
                </p>
                {marketSegment !== 'requests' ? (
                  <span className="marketplace-hub__results-help">
                    Mostrando proveedores disponibles en tiempo real.
                  </span>
                ) : (
                  <span className="marketplace-hub__results-help">
                    Oportunidades publicadas por clientes verificados.
                  </span>
                )}
              </div>
              <div className="marketplace-hub__toolbar-controls">
                <button
                  type="button"
                  className={`marketplace-hub__filter-toggle${hasActiveFilters ? ' is-active' : ''}`}
                  onClick={() => setShowFilters(true)}
                >
                  <FaSlidersH aria-hidden="true" />
                  Filtros
                </button>
                <label htmlFor="marketplace-hub-sort">Ordenar</label>
                <div className="marketplace-hub__sort">
                  <select
                    id="marketplace-hub-sort"
                    value={sortOption}
                    onChange={event => setSortOption(event.target.value)}
                  >
                    <option value="recent">Relevancia</option>
                    <option value="rating">Mejor calificados</option>
                    <option value="priceAsc">Precio (menor)</option>
                    <option value="priceDesc">Precio (mayor)</option>
                  </select>
                </div>
                {marketSegment !== 'requests' && (
                  <div className="marketplace-hub__view-toggle" role="group" aria-label="Cambiar vista">
                    <button
                      type="button"
                      className={viewMode === 'classic' ? 'is-active' : ''}
                      onClick={() => setViewMode('classic')}
                    >
                      <FaListUl aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className={viewMode === 'map' ? 'is-active' : ''}
                      onClick={() => setViewMode('map')}
                    >
                      <FaMapMarkedAlt aria-hidden="true" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {(activeLoading || activeError) && (
              <div className="marketplace-hub__status-area" aria-live="polite">
                {activeLoading ? (
                  <p className="marketplace-hub__status-hint" role="status">
                    Actualizando resultados...
                  </p>
                ) : null}
                {activeError ? (
                  <p className="marketplace-hub__status-hint marketplace-hub__status-hint--error" role="alert">
                    {activeError}
                  </p>
                ) : null}
              </div>
            )}

            {marketSegment === 'requests' ? (
              paginated.length ? (
                <div className="marketplace-hub__requests">
                  {paginated.map(item => {
                    const originLabel = item.pickupLocationDisplay || 'Origen por definir';
                    const destinationLabel = item.dropoffLocationDisplay || 'Destino por definir';
                    return (
                      <Link
                        key={item.id}
                        to={item.href}
                        role="article"
                        aria-label={`Abrir solicitud ${item.title}`}
                        className={`marketplace-hub-request${
                          item.expired ? ' marketplace-hub-request--expired' : ''
                        }`}
                      >
                        <div className="marketplace-hub-request__split">
                          <div className="marketplace-hub-request__info">
                            <div className="marketplace-hub-request__headline">
                              <div className="marketplace-hub-request__title">
                                <h4>{item.title}</h4>
                                <div className="marketplace-hub-request__badge-row">
                                  <span className="marketplace-hub-request__badge">{item.serviceTypeLabel}</span>
                                  <span
                                    className={`marketplace-hub-request__status-pill${
                                      item.expired ? ' is-expired' : ' is-active'
                                    }`}
                                  >
                                    {item.expired ? 'Solicitud expirada' : 'Solicitud activa'}
                                  </span>
                                </div>
                                <p className="marketplace-hub-request__company">
                                  {item.companyName || 'Cliente privado'}
                                </p>
                              </div>
                              <span
                                className={`marketplace-hub-request__price${
                                  item.expired ? ' is-muted' : ''
                                }`}
                              >
                                {item.budgetAmount ? (
                                  <>
                                    <span className="marketplace-hub-request__price-value">
                                      {item.budgetAmount}
                                    </span>
                                    <span className="marketplace-hub-request__price-currency">
                                      {item.currencyCode}
                                    </span>
                                  </>
                                ) : (
                                  item.budgetLabel
                                )}
                              </span>
                            </div>
                            <div
                              className="marketplace-hub-request__route-hero"
                              aria-label={`Ruta de ${originLabel} a ${destinationLabel}`}
                            >
                              <div className="marketplace-hub-request__route-node">
                                <FaRegCircle aria-hidden="true" />
                                <strong>{originLabel}</strong>
                              </div>
                              <div className="marketplace-hub-request__route-track" aria-hidden="true">
                                <span />
                                <FaLongArrowAltRight aria-hidden="true" />
                              </div>
                              <div className="marketplace-hub-request__route-node is-destination">
                                <FaMapMarkerAlt aria-hidden="true" />
                                <strong>{destinationLabel}</strong>
                              </div>
                            </div>
                            <div className="marketplace-hub-request__meta">
                              <p>
                                <FiCalendar aria-hidden="true" />
                                <span>
                                  {item.neededByLabel
                                    ? `Necesita para ${item.neededByLabel}`
                                    : 'Fecha por confirmar'}
                                </span>
                              </p>
                              <p>
                                <FiPackage aria-hidden="true" />
                                <span>{item.loadTypeLabel || item.serviceTypeLabel}</span>
                              </p>
                            </div>
                            <span className="marketplace-hub-request__cta-button">
                              Ver detalle
                              <FaArrowRight aria-hidden="true" />
                            </span>
                          </div>
                          <MarketplaceRequestRouteMap
                            originCoordinates={item.originCoordinates}
                            destinationCoordinates={item.destinationCoordinates}
                            originLabel={originLabel}
                            destinationLabel={destinationLabel}
                          />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                modernEmptyState
              )
            ) : viewMode === 'map' ? (
              <div className="marketplace-hub__map-panel">
                <MarketplaceMapView items={filteredPublications} />
              </div>
            ) : paginated.length ? (
              <div className="marketplace-hub__grid">
                {paginated.map(item => {
                  const publicationHref = `/publicaciones/${item.publicId}`;
                  const tagPool = [
                    item.unitTypeLabel,
                    item.categoria,
                    item.descriptor?.split(' - ')[0],
                    item.descriptor?.split(' - ')[1],
                  ]
                    .filter(Boolean)
                    .map(value => value.trim());
                  const tags = Array.from(new Set(tagPool)).slice(0, 3);
                  return (
                    <article key={item.id} className="marketplace-hub-card">
                      <Link to={publicationHref} className="marketplace-hub-card__media">
                        <img src={item.img} alt={item.imgAlt} loading="lazy" />
                        <span className="marketplace-hub-card__badge">{item.categoria}</span>
                        {item.promoted ? (
                          <span className="marketplace-hub-card__verified" aria-label="Proveedor verificado">
                            <FaShieldAlt aria-hidden="true" />
                          </span>
                        ) : null}
                      </Link>
                      <div className="marketplace-hub-card__body">
                        <p className="marketplace-hub-card__provider">{item.companyName}</p>
                        <div className="marketplace-hub-card__title-row">
                          <h3>
                            <Link to={publicationHref}>{item.nombre}</Link>
                          </h3>
                          <span className="marketplace-hub-card__rating">
                            <FaStar aria-hidden="true" />
                            {(item.rating || 0).toFixed(1)}
                          </span>
                        </div>
                        <p className="marketplace-hub-card__location">
                          <FaMapMarkerAlt aria-hidden="true" /> {item.ubic}
                        </p>
                        <p className="marketplace-hub-card__descriptor">
                          {item.descriptor || item.unitTypeLabel}
                        </p>
                        {tags.length ? (
                          <div className="marketplace-hub-card__tags">
                            {tags.map(tag => (
                              <span key={tag}>{tag}</span>
                            ))}
                          </div>
                        ) : null}
                        <Link to={publicationHref} className="marketplace-hub-card__footer-link">
                          <div className="marketplace-hub-card__footer">
                            <span className="marketplace-hub-card__price-value">{item.priceLabel}</span>
                            <span className="marketplace-hub-card__footer-cta">Ver</span>
                          </div>
                        </Link>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              modernEmptyState
            )}

            {viewMode !== 'map' && totalPages > 1 && (
              <div className="marketplace-hub__pagination">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(page => page - 1)}
                >
                  Anterior
                </button>
                {Array.from({ length: totalPages }, (_, index) => (
                  <button
                    type="button"
                    key={index + 1}
                    className={currentPage === index + 1 ? 'is-active' : ''}
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(page => page + 1)}
                >
                  Siguiente
                </button>
              </div>
            )}
          </section>
        </div>

        {showFilters ? (
          <button
            type="button"
            className="marketplace-hub__filters-backdrop"
            aria-label="Cerrar filtros"
            onClick={closeFiltersPanel}
          />
        ) : null}
      </div>
    );
}

function MarketplaceRequestRouteMap({
  originCoordinates,
  destinationCoordinates,
  originLabel,
  destinationLabel,
}) {
  const { origin: originPoint, destination: destinationPoint } = useRequestRoutePoints(
    originCoordinates,
    destinationCoordinates,
    originLabel,
    destinationLabel
  );

  const fallbackPoint = originPoint || destinationPoint;

  if (!fallbackPoint) {
    return (
      <div className="marketplace-hub-request__map marketplace-hub-request__map--empty" aria-hidden="true">
        <span className="marketplace-hub-request__map-empty-label">Mapa no disponible</span>
      </div>
    );
  }

  const routePositions = [];
  if (originPoint) {
    routePositions.push([originPoint.latitude, originPoint.longitude]);
  }
  if (destinationPoint) {
    routePositions.push([destinationPoint.latitude, destinationPoint.longitude]);
  }

  return (
    <div className="marketplace-hub-request__map" role="presentation" aria-hidden="true">
      <MapContainer
        center={[fallbackPoint.latitude, fallbackPoint.longitude]}
        zoom={6}
        dragging={false}
        doubleClickZoom={false}
        scrollWheelZoom={false}
        touchZoom={false}
        keyboard={false}
        zoomControl={false}
        attributionControl={false}
        className="marketplace-hub-request__map-canvas"
      >
        <TileLayer url={MAP_TILE_URL} attribution={MAP_TILE_ATTRIBUTION} />
        <MiniRouteViewport origin={originPoint} destination={destinationPoint} />
        {routePositions.length >= 2 ? (
          <Polyline
            positions={routePositions}
            pathOptions={{ color: '#2563eb', weight: 4, opacity: 0.85, lineCap: 'round', lineJoin: 'round' }}
          />
        ) : null}
        {originPoint ? (
          <CircleMarker
            center={[originPoint.latitude, originPoint.longitude]}
            radius={6}
            pathOptions={{ color: '#1d4ed8', weight: 2, fillColor: '#1d4ed8', fillOpacity: 0.95 }}
          />
        ) : null}
        {destinationPoint ? (
          <CircleMarker
            center={[destinationPoint.latitude, destinationPoint.longitude]}
            radius={6}
            pathOptions={{ color: '#f97316', weight: 2, fillColor: '#f97316', fillOpacity: 0.95 }}
          />
        ) : null}
      </MapContainer>
    </div>
  );
}

function MiniRouteViewport({ origin, destination }) {
  const map = useMap();

  useEffect(() => {
    if (!map) {
      return undefined;
    }

    const points = [];
    if (origin?.latitude != null && origin?.longitude != null) {
      points.push([origin.latitude, origin.longitude]);
    }
    if (destination?.latitude != null && destination?.longitude != null) {
      points.push([destination.latitude, destination.longitude]);
    }

    if (!points.length) {
      return undefined;
    }

    try {
      if (points.length === 1) {
        map.setView(points[0], 8, { animate: false });
      } else {
        const bounds = L.latLngBounds(points);
        map.fitBounds(bounds.pad(0.3), { animate: false });
      }
    } catch (error) {
      console.warn('No se pudo ajustar la ruta en el mapa de solicitudes', error);
    }

    return undefined;
  }, [map, origin?.latitude, origin?.longitude, destination?.latitude, destination?.longitude]);

  return null;
}

function useRequestRoutePoints(originCoordinates, destinationCoordinates, originLabel, destinationLabel) {
  const [points, setPoints] = useState({
    origin: originCoordinates || null,
    destination: destinationCoordinates || null,
  });

  useEffect(() => {
    let cancelled = false;

    const nextPoints = {
      origin: originCoordinates || null,
      destination: destinationCoordinates || null,
    };

    const jobs = [];

    if (!nextPoints.origin && originLabel) {
      jobs.push(
        resolveLocationLabel(originLabel).then(entry => {
          if (entry?.latitude != null && entry?.longitude != null) {
            nextPoints.origin = { latitude: entry.latitude, longitude: entry.longitude };
          }
        })
      );
    }

    if (!nextPoints.destination && destinationLabel) {
      jobs.push(
        resolveLocationLabel(destinationLabel).then(entry => {
          if (entry?.latitude != null && entry?.longitude != null) {
            nextPoints.destination = { latitude: entry.latitude, longitude: entry.longitude };
          }
        })
      );
    }

    const finalize = () => {
      if (cancelled) {
        return;
      }
      setPoints({
        origin: nextPoints.origin || null,
        destination: nextPoints.destination || null,
      });
    };

    if (!jobs.length) {
      finalize();
    } else {
      Promise.all(jobs)
        .catch(error => {
          console.warn('No se pudieron resolver las coordenadas de la solicitud', error);
        })
        .finally(finalize);
    }

    return () => {
      cancelled = true;
    };
  }, [
    originCoordinates,
    originCoordinates?.latitude,
    originCoordinates?.longitude,
    destinationCoordinates,
    destinationCoordinates?.latitude,
    destinationCoordinates?.longitude,
    originLabel,
    destinationLabel,
  ]);

  return points;
}

function MapBoundsController({ markers }) {
  const map = useMap();

  useEffect(() => {
    if (!map) {
      return undefined;
    }

    let cancelled = false;

    const applyBounds = () => {
      if (cancelled) {
        return;
      }

      if (!markers.length) {
        try {
          map.setView(MAP_DEFAULT_CENTER, MAP_DEFAULT_ZOOM, { animate: false });
        } catch (error) {
          console.warn('No se pudo restablecer la vista del mapa', error);
        }
        return;
      }

      const latitudes = markers
        .map((marker) => marker.latitude)
        .filter((value) => Number.isFinite(value));
      const longitudes = markers
        .map((marker) => marker.longitude)
        .filter((value) => Number.isFinite(value));

      if (!latitudes.length || !longitudes.length) {
        return;
      }

      const south = Math.min(...latitudes);
      const north = Math.max(...latitudes);
      const west = Math.min(...longitudes);
      const east = Math.max(...longitudes);

      const fit = () => {
        const commonOptions = { padding: [24, 24], maxZoom: 11, animate: false };

        if (south === north && west === east) {
          const delta = 0.25;
          map.fitBounds(
            [
              [south - delta, west - delta],
              [north + delta, east + delta],
            ],
            commonOptions
          );
          return;
        }

        map.fitBounds(
          [
            [south, west],
            [north, east],
          ],
          commonOptions
        );
      };

      try {
        fit();
      } catch (error) {
        console.warn('No se pudieron ajustar los limites del mapa', error);
      }
    };

    if (map._loaded) {
      applyBounds();
    } else {
      map.once('load', applyBounds);
    }

    return () => {
      cancelled = true;
      map.off('load', applyBounds);
    };
  }, [markers, map]);

  return null;
}

function MapResizeWatcher({ dependency }) {
  const map = useMap();

  useEffect(() => {
    if (!map) {
      return undefined;
    }

    let cancelled = false;
    let timer = null;

    const invalidate = () => {
      if (cancelled) {
        return;
      }
      timer = setTimeout(() => {
        if (cancelled) {
          return;
        }
        try {
          map.invalidateSize();
        } catch (error) {
          console.warn('No se pudo recalcular el tamano del mapa', error);
        }
      }, 150);
    };

    if (map._loaded) {
      invalidate();
    } else {
      map.once('load', invalidate);
    }

    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
      map.off('load', invalidate);
    };
  }, [map, dependency]);

  return null;
}

function MapMarkers({ markers }) {
  return (markers || [])
    .filter(marker => Number.isFinite(marker.latitude) && Number.isFinite(marker.longitude))
    .map(marker => <MapMarker key={marker.key || `${marker.latitude}-${marker.longitude}-${marker.total}`} marker={marker} />);
}

function MapMarker({ marker }) {
  const map = useMap();
  const markerRef = useRef(null);
  const imageFallback = useImageFallback();

  const clusterItems = Array.isArray(marker.items) ? marker.items : [];
  const primaryItem = clusterItems[0] || {};
  const isCluster = marker.total > 1;
  const sameLocationCluster = isCluster && clusterItemsShareCoords(marker);

  const label = isCluster
    ? `${marker.total}+`
    : formatMarkerLabel(primaryItem.priceLabel || primaryItem.categoria || marker.display || 'Ver');

  const icon = useMemo(
    () =>
      L.divIcon({
        className: `marketplace-map-marker${isCluster ? ' marketplace-map-marker--cluster' : ''}`,
        html: `<span>${label}</span>`,
        iconSize: [1, 1],
        popupAnchor: [0, -30],
      }),
    [label, isCluster]
  );

  const popupImage = primaryItem.img || primaryItem.imageUrl || primaryItem.image || PLACEHOLDER_IMAGE;
  const popupTitle = primaryItem.nombre || primaryItem.title || marker.display || 'Publicación';
  const popupLocation = primaryItem.ubic || primaryItem.locationLabel || marker.name || 'Ubicación no disponible';
  const popupPrice = primaryItem.priceLabel || 'Consulta precio';
  const resolvedId = primaryItem.publicId || primaryItem.numericId || primaryItem.id;
  const publicationHref = resolvedId ? `/publicaciones/${resolvedId}` : '#';

  const limitedClusterItems = clusterItems.slice(0, 10);
  const hasMoreClusterItems = clusterItems.length > limitedClusterItems.length;

  const handleMarkerClick = useCallback(
    event => {
      if (!isCluster) {
        return;
      }
      const currentZoom = map.getZoom();
      const allowPopup = currentZoom >= MAP_MAX_ZOOM || sameLocationCluster;
      if (!allowPopup) {
        event?.originalEvent?.preventDefault();
        event?.originalEvent?.stopPropagation();
        markerRef.current?.closePopup();
        const targetZoom = Math.min(MAP_MAX_ZOOM, currentZoom + MAP_ZOOM_INCREMENT);
        map.flyTo([marker.latitude, marker.longitude], targetZoom, { animate: true, duration: 0.6 });
      }
    },
    [isCluster, map, marker.latitude, marker.longitude, sameLocationCluster]
  );

  return (
    <Marker
      ref={markerRef}
      position={[marker.latitude, marker.longitude]}
      icon={icon}
      eventHandlers={{ click: handleMarkerClick }}
    >
      <Popup className={`marketplace-map-popup${isCluster ? ' marketplace-map-popup--cluster' : ''}`}>
        {isCluster ? (
          <div className="marketplace-map-popup__card marketplace-map-popup__card--cluster">
            <button
              type="button"
              className="marketplace-map-popup__close"
              aria-label="Cerrar"
              onClick={() => markerRef.current?.closePopup()}
            >
              ×
            </button>
            <div className="marketplace-map-popup__cluster-header">
              {marker.total === 1 ? '1 publicacion disponible' : `${marker.total} publicaciones disponibles`}
            </div>
            <div className="marketplace-map-popup__cluster-list">
              {limitedClusterItems.map(item => {
                const clusterId = item.publicId || item.numericId || item.id;
                const clusterHref = clusterId ? `/publicaciones/${clusterId}` : '#';
                const clusterImage = item.img || item.imageUrl || item.image || FALLBACK_THUMBNAIL;
                return (
                  <Link key={`${marker.key || marker.latitude}-${clusterId}`} to={clusterHref} className="marketplace-map-popup__cluster-item">
                    <img src={clusterImage} alt={item.nombre || item.title || 'Publicacion'} loading="lazy" onError={imageFallback} />
                    <div className="marketplace-map-popup__cluster-info">
                      <span className="marketplace-map-popup__cluster-title">{item.nombre || item.title || 'Sin título'}</span>
                      <span className="marketplace-map-popup__cluster-price">{item.priceLabel || 'Consulta precio'}</span>
                    </div>
                    <span className="marketplace-map-popup__cluster-chevron">›</span>
                  </Link>
                );
              })}
              {hasMoreClusterItems ? (
                <div className="marketplace-map-popup__cluster-more" role="presentation">
                  Cargar más...
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="marketplace-map-popup__card">
            <button
              type="button"
              className="marketplace-map-popup__close"
              aria-label="Cerrar"
              onClick={() => markerRef.current?.closePopup()}
            >
              ×
            </button>
            <img src={popupImage} alt={popupTitle} loading="lazy" onError={imageFallback} />
            <div className="marketplace-map-popup__body">
              <Link to={publicationHref} className="marketplace-map-popup__title">
                {popupTitle}
              </Link>
              <p className="marketplace-map-popup__location">
                <FaMapMarkerAlt aria-hidden="true" /> {popupLocation}
              </p>
              <p className="marketplace-map-popup__price">{popupPrice}</p>
            </div>
          </div>
        )}
      </Popup>
    </Marker>
  );
}

function MarketplaceMapView({ items }) {
  const publicationItems = useMemo(() => {
    return items
      .filter(Boolean)
      .map((item) => ({
        id: item.id,
        numericId: item.publicId,
        title: item.nombre,
        companyName: item.companyName,
        location: item.ubic,
        updated: item.fecha,
        typeLabel: item.categoria,
      }));
  }, [items]);

  const { markers, loading } = useActivityMarkers([], publicationItems);
  const locationCount = markers.length;
  const totalPublications = items.length;

  return (
    <div className="marketplace-map-view">
      <div className="marketplace-map-view__map">
        <header className="marketplace-map-view__summary">
          <h3>Publicaciones en mapa</h3>
          <p>
            {totalPublications
              ? `Mostrando ${totalPublications} publicaciones en ${locationCount || 0} ubicaciones`
              : 'No hay publicaciones para mostrar.'}
          </p>
        </header>
        <MapContainer
          center={MAP_DEFAULT_CENTER}
          zoom={MAP_DEFAULT_ZOOM}
          className="marketplace-map-view__leaflet"
          scrollWheelZoom
          maxZoom={MAP_MAX_ZOOM}
        >
          <TileLayer url={MAP_TILE_URL} attribution={MAP_TILE_ATTRIBUTION} />
          <MapResizeWatcher dependency={markers.length} />
          <MapBoundsController markers={markers} />
          <MapMarkers markers={markers} />
        </MapContainer>
        {loading ? (
          <div className="marketplace-map-view__loading">Cargando ubicaciones...</div>
        ) : null}
        {!loading && !markers.length ? (
          <div className="marketplace-map-view__empty">
            No pudimos georreferenciar estas publicaciones. Ajusta la ubicacion o revisa los datos.
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function MarketplacePage() {
  return <MarketplaceContent withinDashboard={false} />;
}
